import { useEffect, useCallback, useRef, useState } from 'react';

// --- CONFIGURATION ---
const SECRET_KEY = "Vectro";
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const STORAGE_KEY = "vectro_ticket_session";

// --- LIMITS ---
const STATIC_ID_MAX = 16383;    // 14 bits
const STATIC_SEQ_MAX = 16777215;// 24 bits
const BATCH_LIMIT = 512;        // 9 bits (Increased from 256)

interface PersistedState {
  sessionId: string;
  sequence: number;
}

export function useTicketGenerator(siteId: number) {
  const keyRef = useRef<CryptoKey | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (siteId !== 0 && siteId !== 1) console.error("Invalid Site ID");

    let active = true;
    async function init() {
      try {
        const enc = new TextEncoder();
        const keyHash = await window.crypto.subtle.digest('SHA-256', enc.encode(SECRET_KEY));
        const importedKey = await window.crypto.subtle.importKey(
          'raw', keyHash, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
        );
        if (active) {
          keyRef.current = importedKey;
          setIsReady(true);
        }
      } catch (err) { console.error(err); }
    }
    init();
    return () => { active = false; };
  }, [siteId]);

  // --- STATE MANAGEMENT ---

  const getNextState = useCallback((): { session: bigint, seq: number } => {
    let currentSession = 0n;
    let currentSeq = 0;

    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const data: PersistedState = JSON.parse(raw);
        currentSession = BigInt(data.sessionId);
        currentSeq = data.sequence;
      } catch (e) { console.warn("Resetting state"); }
    }

    // CHECK LIMIT: 512
    if (currentSeq >= BATCH_LIMIT || raw === null) {
      // ROTATE: Generate new 29-bit Session
      const randBuf = new Uint32Array(1);
      window.crypto.getRandomValues(randBuf);
      // Mask to 29 bits: 0x1FFFFFFF
      currentSession = BigInt(randBuf[0] & 0x1FFFFFFF);
      currentSeq = 0;
    }

    return { session: currentSession, seq: currentSeq };
  }, []);

  const saveState = useCallback((session: bigint, nextSeq: number) => {
    const newState: PersistedState = {
      sessionId: session.toString(),
      sequence: nextSeq
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  }, []);

  // --- GENERATOR ---
  const _coreGenerate = useCallback(async (
    isStatic: boolean,
    identityId: number,
    sequence: number,
    dynamicSession?: bigint
  ): Promise<string> => {

    if (!keyRef.current) throw new Error("Initializing...");

    const bSite = BigInt(siteId);
    let rawId = 0n;

    if (isStatic) {
      // MODE 0 (Unchanged)
      if (identityId > STATIC_ID_MAX) throw new Error("Static ID limit");
      if (sequence > STATIC_SEQ_MAX) throw new Error("Static Seq limit");
      rawId = (bSite << 38n) | (BigInt(identityId) << 24n) | BigInt(sequence);

    } else {
      // MODE 1 (Updated)
      const bSession = dynamicSession!;

      // [1] [Site(1)] [Session(29)] [Seq(9)]
      // Shift logic:
      // Mode: Bit 40 (1 << 39)
      // Site: Shifted by (29 + 9) = 38
      // Session: Shifted by 9
      rawId = (1n << 39n) | (bSite << 38n) | (bSession << 9n) | BigInt(sequence);
    }

    const scrambled = await feistelEncrypt(rawId, keyRef.current);
    return encodeCrockford(scrambled);
  }, [siteId]);

  // --- EXPOSED FUNCTIONS ---

  const generateStatic = useCallback(async (identityId: number, sequence: number) => {
    const ticket = await _coreGenerate(true, identityId, sequence);
    return `${ticket.slice(0, 4)}-${ticket.slice(4)}`;
  }, [_coreGenerate]);

  const generateDynamic = useCallback(async () => {
    const { session, seq } = getNextState();
    const ticket = await _coreGenerate(false, 0, seq, session);
    saveState(session, seq + 1);
    return `${ticket.slice(0, 4)}-${ticket.slice(4)}`;
  }, [getNextState, _coreGenerate, saveState]);

  return { generateStatic, generateDynamic, isReady };
}

// --- HELPERS (Standard) ---
async function feistelEncrypt(input: bigint, key: CryptoKey): Promise<bigint> {
  const ROUNDS = 4;
  const HALF_MASK = 0xFFFFFn;
  let left = (input >> 20n) & HALF_MASK;
  let right = input & HALF_MASK;

  for (let i = 0; i < ROUNDS; i++) {
    const buf = new ArrayBuffer(12);
    const view = new DataView(buf);
    view.setInt32(0, i);
    view.setBigInt64(4, right);
    const sig = await window.crypto.subtle.sign('HMAC', key, buf);
    const hashVal = new DataView(sig).getBigUint64(0);
    const newRight = left ^ (hashVal & HALF_MASK);
    left = right;
    right = newRight;
  }
  return (left << 20n) | right;
}

function encodeCrockford(number: bigint): string {
  let res = "";
  for (let i = 0; i < 8; i++) {
    res = ALPHABET[Number(number & 31n)] + res;
    number >>= 5n;
  }
  return res;
}
